import { useState, useEffect } from 'react';
import { Button, Grid, LinearProgress, Skeleton } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
// adding App Insights hook import for telemetry logging
import { useAppInsights } from 'src/hooks/useAppInsights';
import { getDataForSimulatedRows, getDataForSimulatedRowsWithBaseline } from 'src/components/collapsible-table/table-data-prep';
import {
  getDefaultOptionsForFormFields,
  IFilterObj
} from 'src/components/filters';
import { transformDynamicFormValueToFilterPayload } from 'src/components/filters/utils/filter-form-utils';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import { getTableColumnData } from 'src/components/collapsible-table/table-column-prep';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import RenovationTableComponent from './renovation-table/renovation-table';
import {
  getRenovationTableData,
  handleSimulationFailedFlagValue,
  handleRenovationSimulationFlagValue,
  saveRenovationSimulationData,
  resetRenovationSimulationData
} from './store/renovation-slice';
import SimulatorFooter from '../components/simulation-footer/simulator-footer';
import ScenarioCreateSuccessDialog from '../home/components/scenario-create-success/scenario-create-success-dialog';
import { formatNumber } from '../pricing-simulator';
import DrawerBoxContainer from '../pricing-simulator/components/comment-box-container/drawer-box-container';
import {
  InputType,
  MODIFIED_ROW_INDICATOR_KEY,
  SummaryStripMasterData,
  TableComponent
} from '../pricing-simulator/types/generic-table.model';
import SaveAsContainer from '../components/save-as-container/save-as-container';
import useModulePreRequisites from '../common/hooks/use-module-pre-requisites.hook';
import MODULE_TO_PAGE_MAP from '../home/types/simulation-module-to-page-map';
import {
  FiltersWrapper,
  setGlobalFilterFieldOptions,
  setGlobalFilterValues
} from '../common/features';
import { setTableMetaData } from '../common/features/table/store/table-slice';
import getOutputTableColumn from '../common/features/table/components/output-column-prep';
import TableSkeletonContainer from '../pricing-simulator/components/simulation-table-container/table-skeleton';
import { MatomoEventTrackMapObject } from '../constants/constant';
import OnNextClickHandler from '../common/utils/simulator-page-next-button';
import { AlertDialog, DialogResult } from '../../../../../src/components/analytics/alert-dialog/index';



export default function Renovation() {
  const appDispatch = useAppDispatch();

  // adding App Insights hook initialization for telemetry logging
  const { trackEvent, trackException } = useAppInsights();

  const { isDisplayComments } = useAppSelector((state) => state.drawerManager);
  const {
    renovationTableData,
    isScenarioSaving,
    isSimulationFailed,
    enableSimulation,
    isSimulating,
    fetchingTableData,
    isScenarioSavedSuccessfully,
    lastSimulatedSnapshot, //Snapshot after last simulation
  } = useAppSelector((state) => state.renovation);
  const {
    globalFilterCombinations,
    globalFilterFormFields,
    globalFilterValue,
    globalFilterInitialValues
  } = useAppSelector((state) => state.filters);
  const summaryStripData: SummaryStripMasterData =
    renovationTableData.summary_strip;
  const { renovation: renovationAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );

  const { tableMetadaData } = useAppSelector((state) => state.table);
  const [flagSimulation, setFlagSimulation] = useState<boolean>(false);

  const [columns, setColumns] = useState([]);

  // moving moduleId, sourceId, scenarioId to top to avoid "used before defined" errors
  const { sourceId, moduleId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.RENOVATION
  });

  const getColumnsData = (tableResData: any) => {
    const { inputCols, resultCols, response } = tableResData;
    appDispatch(setTableMetaData({ tableData: response }));
    setColumns([
      ...getTableColumnData(
        inputCols,
        TableComponent.RENOVATION,
        response.header,
        [],
        !renovationAccessConfig.provide_inputs,
        moduleId,
        sourceId
      ),
      ...getOutputTableColumn(
        resultCols,
        response.output_toggle,
        response.footer.row,
        {},
        TableComponent.RENOVATION
      )
    ]);
  };
  const { dynamicBreadcrumbs } = useAppSelector((state) => state.simulatorHome);
  const [externalReset, setExternalReset] = useState(false);
  const [isFilterValueChanged, setIsFilterValueChanged] = useState(false);
  const { isFetchingPreRequisites } = useModulePreRequisites({
    module: MODULE_TO_PAGE_MAP.RENOVATION
  });

  const [isScenarioFailureDialogOpen, setIsScenarioFailureDialogOpen] =
    useState(false);

  const handleScenarioFailureDialogClose = () => {
    setIsScenarioFailureDialogOpen(false);
  };
  const { callFunction } = useMatomoFunction();
  const getPageName = () => {
    const page_title = window.location.href.split('/').at(-1);
    return page_title;
  };

  // moving currentScenarioId to top to avoid "used before defined" errors
  const { id: currentScenarioId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    callFunction(
      MatomoEventTrackMapObject.track_page_view.event_key,
      getPageName()
    );

    // adding telemetry log for page load with business context
    trackEvent('Renovation_PageLoaded', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      hasSavedScenario: !!currentScenarioId,
      accessLevel: renovationAccessConfig?.save ? 'full' : 'read-only'
    });
  }, []);

  useEffect(() => {
    if (isSimulationFailed) {
      setIsScenarioFailureDialogOpen(true);
      appDispatch(handleSimulationFailedFlagValue());
    }
  }, [isSimulationFailed]);

  const [scenarioCreateSuccessInfo, setScenarioCreateSuccessInfo] = useState({
    isDialogOpen: false,
    scenario: null,
    message: null
  });

  const [scenarioSimulationSavingInfo, setScenarioSimulationSavingInfo] =
    useState({
      isDialogOpen: false,
      scenario: null,
      message: null
    });

  const [scenarioSimulationInfo, setScenarioSimulationInfo] = useState({
    isDialogOpen: false,
    scenario: null,
    message: null
  });

  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const dialogMessage = "You have made changes on this page that haven't been simulated yet. Please click 'Simulate' to apply \nthem. If you proceed to the next module without simulating, these changes will be discarded. Are you \nsure you want to continue?"
  const dialogTitle = ""

  const [pendingNavigation, setPendingNavigation] = useState<{
    type: 'next' | 'breadcrumb';
    target?: string;
  } | null>(null);


  const checkUnsavedChanges = (compareAgainstSnapshot = false) => {
    const result = getChangedInputRows(compareAgainstSnapshot);
    return result.isChanged;
  };

  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES && pendingNavigation) {
      if (pendingNavigation.type === 'next') {
        OnNextClickHandler(
          dynamicBreadcrumbs,
          MODULE_TO_PAGE_MAP.RENOVATION,
          currentScenarioId,
          navigate
        );
      } else if (pendingNavigation.type === 'breadcrumb' && pendingNavigation.target) {
        navigate(pendingNavigation.target);
      }
    }
    setConfirmationDialogOpen(false);
    setPendingNavigation(null);
  };

  useEffect(() => {
    setTimeout(() => {
      if (isSimulating) {
        setScenarioSimulationInfo({
          isDialogOpen: true,
          scenario: null,
          message: 'Simulating'
        });
      } else {
        setScenarioSimulationInfo({
          ...scenarioSimulationInfo,
          isDialogOpen: false
        });
      }
    }, 500);
  }, [isSimulating]);

  useEffect(() => {
    setTimeout(() => {
      if (isScenarioSavedSuccessfully) {
        setScenarioSimulationSavingInfo({
          ...scenarioSimulationSavingInfo,
          isDialogOpen: false
        });
        setScenarioCreateSuccessInfo({
          isDialogOpen: true,
          scenario: null,
          message: 'Scenario has been successfully Saved'
        });
      }
    }, 500);
    setTimeout(() => {
      setScenarioCreateSuccessInfo({
        ...scenarioSimulationSavingInfo,
        isDialogOpen: false
      });
    }, 1000);
  }, [isScenarioSavedSuccessfully]);

  useEffect(() => {
    setTimeout(() => {
      if (isScenarioSaving) {
        setScenarioSimulationSavingInfo({
          isDialogOpen: true,
          scenario: null,
          message: 'Saving'
        });
      }
    }, 500);
  }, [isScenarioSaving]);

  const handleClickOpen = () => {
    // adding telemetry log for scenario save initiation
    trackEvent('Renovation_ScenarioSaveInitiated', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      hasSimulated: !!lastSimulatedSnapshot
    });

    appDispatch(
      saveRenovationSimulationData({
        sourceId,
        moduleId,
        scenarioId
      })
    );
  };

  const getChangedInputRows = (compareAgainstSnapshot = false) => {
    // Only compare against snapshot if explicitly requested (Next/Breadcrumb)
    // AND a snapshot actually exists
    if (compareAgainstSnapshot && lastSimulatedSnapshot) {
      const simulatedData = getDataForSimulatedRowsWithBaseline(
        tableMetadaData,
        lastSimulatedSnapshot
      );
      return {
        isChanged: simulatedData.inputs.length > 0,
        row: simulatedData
      };
    }
    const idColumns: string[] = tableMetadaData.header
      .filter(
        (data) =>
          !data.parent.is_visible &&
          data.parent.field_format === InputType.NUMBER
      )
      .sort((a, b) => a.parent.level - b.parent.level)
      .map((data) => data.parent.parent_field);
    const simulatedData = getDataForSimulatedRows(
      tableMetadaData,
      idColumns,
      MODIFIED_ROW_INDICATOR_KEY.RENOVATION_IS_EDITED,
      MODIFIED_ROW_INDICATOR_KEY.RENOVATION_IS_NOT_DEFAULT,
      MODIFIED_ROW_INDICATOR_KEY.RENOVATION_IS_USER_INPUT
    );
    return {
      isChanged: simulatedData.inputs.length > 0 || simulatedData.data.length > 0,
      row: simulatedData
    };
  };

  /**
   * Handler for simulate button click
   */
  const handleSimulationClick = () => {
    // adding telemetry log for simulation initiation with context
    trackEvent('Renovation_SimulationInitiated', {
      hasFilters: Object.keys(globalFilterValue || {}).length > 0,
      scenarioId: String(scenarioId || 'new'),
      sourcePage: window.location.pathname,
      isSimulating
    });

    callFunction(
      MatomoEventTrackMapObject.track_simulate_button_click.event_key,
      getPageName()
    );
    const result = getChangedInputRows();
    if (!result.isChanged) {
      // adding telemetry log for skipped simulation due to no changes
      trackEvent('Renovation_SimulationSkipped', {
        reason: 'no_changes_detected'
      });
      return;
    }
    const dataPoints = {
      filter_inputs: transformDynamicFormValueToFilterPayload({
        formValue: globalFilterValue,
        formFields: globalFilterFormFields,
        combinations: globalFilterCombinations
      }),
      row: result.row
    };
    appDispatch(handleRenovationSimulationFlagValue());
    appDispatch(
      getRenovationTableData({
        dataInput: dataPoints,
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res: any) => {
      // adding telemetry log for simulation completion or error
      if (res?.meta?.requestStatus === 'rejected') {
        trackException(new Error(res['error']?.message || 'Unknown error'), {
          context: 'Renovation_Simulation',
          scenarioId: String(scenarioId || 'new'),
          moduleId: String(moduleId || '')
        });
      } else {
        const payload = res.payload as { tableData?: { row?: any[] } };
        trackEvent('Renovation_SimulationCompleted', {
          rowCount: payload?.tableData?.row?.length || 0,
          scenarioId: String(scenarioId || 'new')
        });
      }
      getColumnsData(res.payload);
    });
  };

  //the handler function for the next button functionality
  const handleNextFn = () => {
    OnNextClickHandler(
      dynamicBreadcrumbs,
      MODULE_TO_PAGE_MAP.RENOVATION,
      currentScenarioId,
      navigate
    );
  };

  const handleBreadcrumbClick = (routeURL: string): boolean => {
    // If same module, allow navigation without dialog
    if (routeURL === window.location.pathname) {
      return true;
    }
    if (!checkUnsavedChanges(true)) {
      return true;
    }
    setPendingNavigation({ type: 'breadcrumb', target: routeURL });
    setConfirmationDialogOpen(true);
    return false;
  };

  const onNextClick = () => {
    if (!checkUnsavedChanges(true)) {
      OnNextClickHandler(
        dynamicBreadcrumbs,
        MODULE_TO_PAGE_MAP.RENOVATION,
        currentScenarioId,
        navigate
      );
      return;
    }
    setPendingNavigation({ type: 'next' });
    setConfirmationDialogOpen(true);
  };


  /**
   * Handler for simulation reset
   */
  const handleSimulationReset = () => {
    // adding telemetry log for simulation reset action
    trackEvent('Renovation_SimulationReset', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      hasUnsavedChanges: getChangedInputRows().isChanged
    });

    setExternalReset(true);
    setTimeout(() => {
      setExternalReset(false);
    }, 2000);
    appDispatch(setGlobalFilterValues(globalFilterInitialValues));
    appDispatch(
      setGlobalFilterFieldOptions(
        getDefaultOptionsForFormFields(
          globalFilterFormFields,
          globalFilterCombinations,
          globalFilterInitialValues
        )
      )
    );
    appDispatch(
      resetRenovationSimulationData({
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res: any) => {
      getColumnsData(res.payload);
    });
  };

  const handleScenarioCreateSuccessDialogClose = () => {
    setScenarioCreateSuccessInfo({
      ...scenarioCreateSuccessInfo,
      isDialogOpen: false
    });
  };
  const handleSimulationDialogClose = () => { };

  const getValue = (key: string) => {
    const getMetricsInfo = summaryStripData.metadata.find(
      (metrics) => metrics.name === key
    );
    return summaryStripData.data.renovation
      ? getMetricsInfo
        ? formatNumber(
          getMetricsInfo.number_format,
          summaryStripData.data.renovation.value
        )
        : summaryStripData.data.renovation.value
      : 0;
  };

  const handleSimulationSavingDialogClose = () => { };

  const handleFilterApply = (filters: IFilterObj[]) => {
    // adding telemetry log for filter apply action
    trackEvent('Renovation_FiltersApplied', {
      filterCount: filters?.length || 0,
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || '')
    });

    appDispatch(
      getRenovationTableData({
        dataInput: {
          filter_inputs: filters,
          row: []
        },
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res: any) => {
      getColumnsData(res.payload);
    });
  };

  if (isFetchingPreRequisites) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid sx={{ padding: (theme) => theme.spacing(1, 2) }}>
        <FiltersWrapper
          accessConfig={renovationAccessConfig}
          externalReset={externalReset}
          moduleId={moduleId}
          onFilterFieldsFetched={handleFilterApply}
          onValueApply={handleFilterApply}
          scenarioId={scenarioId}
          setIsFilterValueChanged={(value) => {
            setIsFilterValueChanged(value);
          }}
          sourceId={sourceId}
        />
        <Grid container my={1} justifyContent='space-between'>
          <Grid item>
            <Grid container spacing={2}>
              <Grid alignSelf='center' item />
              <Grid
                alignSelf='center'
                item
                style={{ paddingLeft: '0px', fontSize: '12px' }}
              >
                {fetchingTableData && (
                  <Skeleton
                    animation='wave'
                    variant='rectangular'
                    width={250}
                    height={25}
                  />
                )}
                {!fetchingTableData && (
                  <div className='info-box-div'>
                    <div
                      className='info-box info-box-label sku-delist-label-container'
                      style={{ padding: '5px 10px 5px 10px' }}
                    >
                      <span style={{ display: 'inline-block', width: '150px' }}>
                        SKU’s Renovated :
                        <span
                          className='info-value'
                          style={{ marginLeft: '15px' }}
                        >
                          {getValue('n_renovated')}
                        </span>{' '}
                      </span>
                    </div>
                  </div>
                )}
              </Grid>
              <Grid item xs='auto' alignSelf='center'>
                <div
                  style={{
                    height: '40px',
                    width: '1px'
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent='flex-end'
            alignContent='end'
            spacing={2}
            item
            xs='auto'
          >
            {' '}
            {/* eslint-disable arrow-body-style  */}
            {renovationTableData.legend.metadata.length > 0 &&
              renovationTableData.legend.metadata.map((legendData, i) => {
                return (
                  <Grid item key={i}>
                    <div className='sku-legend-div' style={{ margin: 'auto' }}>
                      <div
                        className='sku-legend-box'
                        style={{
                          background: `${legendData.color}`,
                          marginTop: '4px'
                        }}
                      />
                      <div
                        className='legend-title'
                        style={{ marginTop: '7px' }}
                      >
                        {legendData.label}{' '}
                        {/* <span className='legend-subtitle'>(from Planner)</span> */}
                      </div>
                    </div>
                  </Grid>
                );
              })}
            <Grid item>
              <SaveAsContainer disabled={!renovationAccessConfig?.save_as} />
            </Grid>
            <Grid item>
              <div>
                <Button
                  variant='contained'
                  onClick={handleClickOpen}
                  disabled={!enableSimulation || !renovationAccessConfig?.save}
                >
                  Save
                </Button>
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            <Grid container>
              {(fetchingTableData || !columns.length) && (
                <Grid item xs={12}>
                  <TableSkeletonContainer rowLength={10} headerLength={6} />
                </Grid>
              )}
              {!fetchingTableData && columns.length > 0 && (
                <Grid item xs={12}>
                  <RenovationTableComponent columns={columns} />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
        <ScenarioCreateSuccessDialog
          isOpen={scenarioCreateSuccessInfo.isDialogOpen}
          imagePath='/static/images/success.svg'
          message={scenarioCreateSuccessInfo.message}
          onClose={handleScenarioCreateSuccessDialogClose}
        />
        <ScenarioCreateSuccessDialog
          isOpen={scenarioSimulationInfo.isDialogOpen}
          imagePath='/static/images/file-text.svg'
          message={scenarioSimulationInfo.message}
          onClose={handleSimulationDialogClose}
        />
        <ScenarioCreateSuccessDialog
          isOpen={scenarioSimulationSavingInfo.isDialogOpen}
          imagePath='/static/images/upload-cloud.svg'
          message={scenarioSimulationSavingInfo.message}
          onClose={handleSimulationSavingDialogClose}
        />
        {isScenarioFailureDialogOpen ? (
          <ScenarioCreateSuccessDialog
            imagePath='/static/images/error.svg'
            isOpen={isScenarioFailureDialogOpen}
            onClose={handleScenarioFailureDialogClose}
            message='Failed To load data'
          />
        ) : null}
      </Grid>
      {isDisplayComments ? <DrawerBoxContainer /> : null}
      <SimulatorFooter
        simulationClickHandler={handleSimulationClick}
        resetSimulationHandler={handleSimulationReset}
        isSimulationDisabled={
          flagSimulation || !renovationAccessConfig.simulate || isFilterValueChanged
        }
        nextClickHandler={onNextClick}
        isButtonSectionVisible
        isResetSimulationDisabled={!renovationAccessConfig.reset_simulation}
        onBreadcrumbClick={handleBreadcrumbClick}
      />
      <AlertDialog
        closeHandler={handleConfirmationDialogClose}
        isDialogOpen={isConfirmationDialogOpen}
        message={dialogMessage}
        title={dialogTitle}
      />
    </>
  );
}